/**
 * Retrospective activity-session dialog. The controlled task/open props initialize interval,
 * completion, emotion, attributes, flow notes, and actual P/RJ/resistance/focus values. Loads
 * attribute definitions while open, submits through training.logActivitySession, and returns the
 * normalized saved activity through onLogged. The same time range can also be logged for other
 * activities done concurrently; the server records those as one concurrency group.
 *
 * @module apps/web/src/components/activities/ActivitySessionLogger
 */

import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import type React from 'react';
import type { AttributeDef, TaskUI } from '@fluxability/types';
import { taskToUI } from '@fluxability/types';
import { api } from '@/lib/api';
import { EnumAttributeInput } from '@/components/attributes/EnumAttributeInput';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useTasksStore } from '@/stores/tasksStore';
import { ConcurrentActivityPicker } from '@/components/activities/ConcurrentActivityPicker';
import { cn } from '@/lib/utils';
import { SavedActivitySessions } from '@/components/activities/SavedActivitySessions';
import { AfterFirstPaint } from '@/hooks/afterFirstPaint';
import { Loader2 } from 'lucide-react';

/** Selected activity, controlled visibility, and save/close callbacks for retrospective session logging. */
interface ActivitySessionLoggerProps {
  /** Activity receiving the retrospective session, or null while unselected. */
  task: TaskUI | null;
  /** Controlled dialog visibility; opening resets session form values. */
  open: boolean;
  /** Request dialog closure. */
  onClose: () => void;
  /** Receive the normalized saved activity after a successful session write. */
  onLogged: (task: TaskUI) => void;
}

/**
 * Retrospective activity-session dialog. The controlled task/open props initialize interval,
 * completion, emotion, attributes, flow notes, and actual P/RJ/resistance/focus values. Loads
 * attribute definitions while open, submits through training.logActivitySession, and returns the
 * normalized saved activity through onLogged.
 */
export function ActivitySessionLogger({
  task,
  open,
  onClose,
  onLogged,
}: ActivitySessionLoggerProps) {
  const { toast } = useToast();
  const [startedAt, setStartedAt] = useState('');
  const [endedAt, setEndedAt] = useState('');
  const [completionPercent, setCompletionPercent] = useState(0);
  const [emotionWord, setEmotionWord] = useState('');
  const [attributes, setAttributes] = useState<AttributeDef[]>([]);
  const [customAttributes, setCustomAttributes] = useState<Record<string, unknown>>({});
  const [flowDepth, setFlowDepth] = useState<number | null>(null);
  const [flowNote, setFlowNote] = useState('');
  const [notes, setNotes] = useState('');
  const [travelMinutes, setTravelMinutes] = useState('');
  const [travelFrom, setTravelFrom] = useState('');
  const [actualLocation, setActualLocation] = useState('');
  const [startingEnergy, setStartingEnergy] = useState('');
  const [concurrentTaskIds, setConcurrentTaskIds] = useState<string[]>([]);
  const updateTask = useTasksStore(s => s.updateTask);

  const [actualState, setActualState] = useState({
    productivityInvestment: null as number | null,
    rejuvenationInvestment: null as number | null,
    productivityReturn: null as number | null,
    rejuvenationReturn: null as number | null,
    resistance: null as number | null,
    focusLevel: null as number | null,
  });
  const [isSaving, setIsSaving] = useState(false);

  // Reset from the latest activity record, but only when the dialog opens or the activity changes:
  // a store refresh of the same activity must not wipe the person's unsaved entries.
  const resetForm = useEffectEvent(() => {
    if (!task) return;
    const end = new Date();
    const start = new Date(end.getTime() - Math.max(5, task.estimatedDuration || 30) * 60000);
    setStartedAt(toDateTimeLocal(start));
    setEndedAt(toDateTimeLocal(end));
    setCompletionPercent(Math.max(0, Math.min(100, task.completionPercent ?? 0)));
    setCustomAttributes(task.customAttributes || {});
    setFlowDepth(null);
    setFlowNote('');
    setNotes('');
    setTravelMinutes('');
    setStartingEnergy('');
    setTravelFrom(String(task.customAttributes?.travel_from_location || ''));
    setActualLocation(String(task.customAttributes?.location || ''));
    setConcurrentTaskIds([]);

    setActualState({
      productivityInvestment: null,
      rejuvenationInvestment: null,
      productivityReturn: null,
      rejuvenationReturn: null,
      resistance: null,
      focusLevel: null,
    });
  });
  const taskId = task?.id;
  useEffect(() => {
    if (open && taskId) resetForm();
  }, [open, taskId]);

  useEffect(() => {
    if (!open || !task) return;
    let cancelled = false;
    async function loadAttributes() {
      const response = await api.user.getAttributes();
      if (!cancelled && response.success && Array.isArray(response.data)) {
        setAttributes(response.data);
        const emotionAttr = response.data.find(isEmotionAttribute);
        const taskCustomAttributes = task?.customAttributes || {};
        setEmotionWord(String(getEmotionValue(taskCustomAttributes, emotionAttr) || ''));
      }
    }
    loadAttributes().catch(error =>
      console.error('Failed to load activity session attributes:', error)
    );
    return () => {
      cancelled = true;
    };
  }, [open, task]);

  const emotionAttribute = useMemo(() => attributes.find(isEmotionAttribute), [attributes]);
  const sessionAttributes = useMemo(
    () => attributes.filter(attr => !isEmotionAttribute(attr)),
    [attributes]
  );

  const durationMinutes = useMemo(() => {
    const start = new Date(startedAt);
    const end = new Date(endedAt);
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start)
      return null;
    return Math.max(1, Math.round((end.getTime() - start.getTime()) / 60000));
  }, [startedAt, endedAt]);

  async function handleSubmit() {
    if (!task || !durationMinutes) return;
    setIsSaving(true);
    try {
      const finalCustomAttributes = {
        ...customAttributes,
        ...(emotionAttribute ? { [emotionAttribute.attr_id]: emotionWord } : {}),
        emotion_word: emotionWord || null,
        actual_travel_minutes: travelMinutes === '' ? null : Number(travelMinutes),
        actual_travel_from_location: travelFrom || null,
        actual_location: actualLocation || null,
        pre_productivity_energy: startingEnergy === '' ? null : Number(startingEnergy),
      };
      const response = await api.training.logActivitySession({
        taskId: task.id,
        startedAt: new Date(startedAt).toISOString(),
        endedAt: new Date(endedAt).toISOString(),
        actualDurationMinutes: durationMinutes,
        completionBefore: task.completionPercent ?? 0,
        completionPercent,
        completed: completionPercent >= 100,
        emotionWord,
        customAttributes: finalCustomAttributes,
        notes,
        actualState,
        concurrentTaskIds,
        flowMarkers:
          flowDepth != null || flowNote.trim()
            ? [
                {
                  timestamp: new Date(endedAt).toISOString(),
                  label: 'Flow state',
                  depth: flowDepth,
                  note: flowNote || null,
                },
              ]
            : [],
      });
      if (!response.success) throw new Error(response.error?.message || 'Could not log session');
      const updatedTask = taskToUI(response.data?.task);
      for (const concurrent of response.data?.concurrentSessions ?? []) {
        if (concurrent.task) {
          const companion = taskToUI(concurrent.task);
          updateTask(companion.id, companion);
        }
      }
      onLogged(updatedTask);
      const alsoLogged = response.data?.concurrentSessions?.length ?? 0;
      toast({
        title: 'Activity session logged',
        description: `${durationMinutes} minutes recorded${alsoLogged ? ` for ${alsoLogged + 1} concurrent activities` : ''}. Completion is now ${completionPercent}%.`,
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Could not log session',
        description: error instanceof Error ? error.message : 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  }

  function updateAttributeValue(attr: AttributeDef, rawValue: string) {
    const value = isNumericAttribute(attr) && rawValue !== '' ? Number(rawValue) : rawValue;
    setCustomAttributes(current => ({ ...current, [attr.attr_id]: value }));
    if (isEmotionAttribute(attr)) setEmotionWord(rawValue);
  }

  function handleAttributeUpdated(updated: AttributeDef) {
    setAttributes(current =>
      current.map(attr => (attr.attr_id === updated.attr_id ? updated : attr))
    );
  }

  return (
    <Dialog open={open} onOpenChange={nextOpen => !nextOpen && onClose()}>
      <DialogContent className="!flex !h-dvh !max-h-dvh !w-dvw !max-w-none flex-col overflow-hidden rounded-none border-0 p-0 sm:!h-auto sm:!max-h-[calc(100dvh-2rem)] sm:!w-[calc(100vw-1rem)] sm:!max-w-[720px] sm:rounded-lg sm:border sm:p-6">
        <DialogHeader className="bg-background/96 shrink-0 border-b px-4 pb-3 pr-12 pt-[calc(1rem+env(safe-area-inset-top))] text-left backdrop-blur sm:border-0 sm:bg-transparent sm:p-0 sm:pr-8">
          <DialogTitle>Log Activity Session</DialogTitle>
          <DialogDescription>
            Record work you already did, even when immersion mode was not running.
          </DialogDescription>
        </DialogHeader>

        <div className="native-scroll grid min-h-0 flex-1 gap-4 overflow-y-auto px-4 py-4 sm:px-0 sm:py-0">
          <div>
            <div className="text-sm font-medium">{task?.title}</div>
            <div className="text-muted-foreground text-xs">
              This updates the activity, the calendar block, and the training data.
            </div>
          </div>

          {open && task && <SavedActivitySessions task={task} onCorrected={updated => updateTask(updated.id, updated)} />}

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Started">
              <Input
                type="datetime-local"
                value={startedAt}
                onChange={event => setStartedAt(event.target.value)}
              />
            </Field>
            <Field label="Ended">
              <Input
                type="datetime-local"
                value={endedAt}
                onChange={event => setEndedAt(event.target.value)}
              />
            </Field>
            <Field label="Duration">
              <div
                className={cn(
                  'flex h-10 items-center rounded-md border px-3 text-sm',
                  durationMinutes ? 'text-foreground' : 'text-red-600'
                )}
              >
                {durationMinutes ? `${durationMinutes} min` : 'Check time'}
              </div>
            </Field>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="session-starting-energy">
              Productivity energy before activity (0–10)
            </Label>
            <Input
              id="session-starting-energy"
              type="number"
              min={0}
              max={10}
              step={0.5}
              value={startingEnergy}
              onChange={event => setStartingEnergy(event.target.value)}
              placeholder="Leave blank if unknown"
            />
            <Label htmlFor="session-location">Where did the activity happen?</Label>
            <Input
              id="session-location"
              maxLength={200}
              value={actualLocation}
              onChange={event => setActualLocation(event.target.value)}
              placeholder="e.g. Gym"
            />
            <Label htmlFor="session-travel-from">Where did you travel from?</Label>
            <Input
              id="session-travel-from"
              maxLength={200}
              value={travelFrom}
              onChange={event => setTravelFrom(event.target.value)}
              placeholder="e.g. Home"
            />
            <Label htmlFor="session-travel">Actual travel to this activity (minutes)</Label>
            <Input
              id="session-travel"
              type="number"
              min={0}
              max={720}
              value={travelMinutes}
              onChange={event => setTravelMinutes(event.target.value)}
              placeholder="Leave blank if unknown"
            />
            <p className="text-muted-foreground text-xs">
              Record travel separately from the activity's start and end times.
            </p>
          </div>
          <Field label="Also did during this time">
            <ConcurrentActivityPicker
              primaryTaskId={task?.id}
              selectedIds={concurrentTaskIds}
              onChange={setConcurrentTaskIds}
            />
          </Field>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Overall completion">
              <Input
                type="number"
                min={0}
                max={100}
                value={completionPercent}
                onChange={event =>
                  setCompletionPercent(clampNumber(event.target.value, 0, 100) ?? 0)
                }
              />
            </Field>
            <Field label="Emotion">
              {emotionAttribute ? (
                <EnumAttributeInput
                  attr={emotionAttribute}
                  value={emotionWord}
                  onChange={value => {
                    setEmotionWord(value);
                    updateAttributeValue(emotionAttribute, value);
                  }}
                  onAttributeUpdated={handleAttributeUpdated}
                />
              ) : (
                <Input
                  value={emotionWord}
                  onChange={event => setEmotionWord(event.target.value)}
                  placeholder="thoughtful"
                />
              )}
            </Field>
            <Field label="Focus">
              <ScoreInput
                value={actualState.focusLevel}
                onChange={value => setActualState(s => ({ ...s, focusLevel: value }))}
              />
            </Field>
          </div>

          {/* Attribute, outcome and note fields render after the dialog's first frame. */}
          <AfterFirstPaint>
          {sessionAttributes.length > 0 && (
            <div className="grid gap-2">
              <div>
                <div className="text-sm font-medium">Session attributes</div>
                <div className="text-muted-foreground text-xs">
                  These feed the same specificity model as the activity fields.
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {sessionAttributes.map(attr => (
                  <Field key={attr.attr_id} label={attr.name}>
                    {attr.data_type === 'enum' ? (
                      <EnumAttributeInput
                        attr={attr}
                        value={customAttributes[attr.attr_id] ?? ''}
                        onChange={value => updateAttributeValue(attr, value)}
                        onAttributeUpdated={handleAttributeUpdated}
                      />
                    ) : (
                      <Input
                        type={isNumericAttribute(attr) ? 'number' : 'text'}
                        value={String(customAttributes[attr.attr_id] ?? '')}
                        onChange={event => updateAttributeValue(attr, event.target.value)}
                      />
                    )}
                  </Field>
                ))}
              </div>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-5">
            <Field label="P invested">
              <ScoreInput
                value={actualState.productivityInvestment}
                onChange={value => setActualState(s => ({ ...s, productivityInvestment: value }))}
              />
            </Field>
            <Field label="RJ invested">
              <ScoreInput
                value={actualState.rejuvenationInvestment}
                onChange={value => setActualState(s => ({ ...s, rejuvenationInvestment: value }))}
              />
            </Field>
            <Field label="P return">
              <ScoreInput
                value={actualState.productivityReturn}
                onChange={value => setActualState(s => ({ ...s, productivityReturn: value }))}
              />
            </Field>
            <Field label="RJ return">
              <ScoreInput
                value={actualState.rejuvenationReturn}
                onChange={value => setActualState(s => ({ ...s, rejuvenationReturn: value }))}
              />
            </Field>
            <Field label="Resistance">
              <ScoreInput
                value={actualState.resistance}
                onChange={value => setActualState(s => ({ ...s, resistance: value }))}
              />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-[160px_1fr]">
            <Field label="Flow depth">
              <ScoreInput value={flowDepth} onChange={setFlowDepth} />
            </Field>
            <Field label="Flow note">
              <Input
                value={flowNote}
                onChange={event => setFlowNote(event.target.value)}
                placeholder="When did flow happen?"
              />
            </Field>
          </div>

          <Field label="Notes">
            <Textarea
              value={notes}
              onChange={event => setNotes(event.target.value)}
              placeholder="What happened during this session?"
            />
          </Field>
          </AfterFirstPaint>
        </div>

        <DialogFooter className="bg-background/96 shrink-0 gap-2 border-t px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur sm:border-0 sm:bg-transparent sm:p-0">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving || !durationMinutes || !task}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Log session
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Place a session-log control below its visible field caption; the caller supplies the control and its value handling. */
function Field({
  label,
  children,
}: {
  /** Visible caption describing this control or metric. */
  label: string; /** Controls or content supplied by the owning view. */
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

/** Edit an optional outcome score in half-point steps. Empty or invalid input becomes null and numeric input is clamped to the 0–10 reporting scale. */
function ScoreInput({
  value,
  onChange,
}: {
  /** Current observed score; null displays an empty input. */
  value:
    | number
    | null; /** Receive the parsed, clamped score or null when the input is empty or invalid. */
  onChange: (value: number | null) => void;
}) {
  return (
    <Input
      type="number"
      min={0}
      max={10}
      step={0.5}
      value={value ?? ''}
      onChange={event => onChange(clampNumber(event.target.value, 0, 10))}
    />
  );
}

/** Recognize supported legacy and current numeric attribute type names before choosing the session outcome input. */
function isNumericAttribute(attr: AttributeDef): boolean {
  return ['Number', 'float', 'integer'].includes(attr.data_type);
}

/** Recognize the emotion field after normalizing its display name, keeping historical field naming compatible with session outcome logging. */
function isEmotionAttribute(attr: AttributeDef): boolean {
  return normalizeAttributeName(attr.name) === 'emotion';
}

/** Normalize an outcome-field name to lowercase underscore-separated text before identifying special emotion handling. */
function normalizeAttributeName(name: unknown): string {
  return String(name ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

/** Prefer the declared emotion attribute’s ID value, then accept retained emotion_word/emotion/emotion_label aliases. Missing evidence becomes an empty draft. */
function getEmotionValue(
  customAttributes: Record<string, unknown>,
  emotionAttribute?: AttributeDef
): unknown {
  if (emotionAttribute) {
    const direct = customAttributes[emotionAttribute.attr_id];
    if (direct !== null && direct !== undefined && direct !== '') return direct;
  }
  return (
    customAttributes.emotion_word ||
    customAttributes.emotion ||
    customAttributes.emotion_label ||
    ''
  );
}

/** Parse an optional score and clamp finite values to the supplied bounds; empty or nonfinite input returns null. */
function clampNumber(value: string, min: number, max: number): number | null {
  if (value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.max(min, Math.min(max, numeric));
}

/** Represent a valid Date in the browser-local datetime input format without the UTC suffix. */
function toDateTimeLocal(date: Date): string {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}
